import React, { useMemo } from "react";
import "./Terms.css";
import { useLocalContext } from "../../context/LocalContext";

const Terms = () => {
  const { webinfo = {} } = useLocalContext();

  const brand = useMemo(
    () => ({
      name: webinfo.name || "digivirtuehost.com",
      email: webinfo.email || "contact@digivirtuehost.com",
      phone: webinfo.phone || "+1 (000) 000-0000",
      phonecall: webinfo.phonecall || "+10000000000",
      address: webinfo.address || "United States",
    }),
    [webinfo]
  );

  const effectiveDate = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <section className="terms">
      <div className="terms-wrap">
        <header className="terms-head">
          <span className="terms-kicker">Legal — United States</span>
          <h1>Terms of Service</h1>
          <p className="terms-meta">
            <span>Provider: {brand.name}</span>
            <span>Effective / last updated: {effectiveDate}</span>
          </p>
          <p className="terms-lead">
            These Terms of Service (“Terms”) govern your access to and use of the
            websites, products, and services offered by {brand.name} (“we,” “us,” or
            “our”) in the United States. By using our services, you agree to these
            Terms. If you do not agree, do not use our services.
          </p>
        </header>

        <div className="terms-content">
          <TermsBlock title="1. Who may use our services">
            <p>
              You must be at least 18 years old (or the age of majority in your
              jurisdiction) and able to form a binding contract under applicable U.S.
              law. If you use our services on behalf of a company or other entity,
              you represent that you have authority to bind that entity to these
              Terms.
            </p>
          </TermsBlock>

          <TermsBlock title="2. Our services">
            <p>
              {brand.name} provides web hosting, related infrastructure, and
              ancillary services as described on our website or in an order,
              statement of work, or other written agreement (“Service Order”). Features,
              limits, and pricing depend on the plan or product you select. We may
              modify, suspend, or discontinue features where reasonably necessary for
              security, legal compliance, or operational reasons, with notice when
              required by law.
            </p>
          </TermsBlock>

          <TermsBlock title="3. Accounts & security">
            <p>
              You are responsible for maintaining the confidentiality of account
              credentials, for all activity under your account, and for providing
              accurate registration and billing information. You must notify us
              promptly of any unauthorized access or security incident related to
              your account.
            </p>
          </TermsBlock>

          <TermsBlock title="4. Acceptable use (U.S. law & policy)">
            <p>You agree not to use our services to:</p>
            <ul>
              <li>
                Violate any applicable federal, state, or local U.S. law or regulation,
                or any law applicable to you or your end users.
              </li>
              <li>
                Infringe intellectual property, privacy, publicity, or other rights
                of others.
              </li>
              <li>
                Transmit malware, conduct unauthorized access or denial-of-service
                attacks, or interfere with the integrity or performance of our
                systems or third-party networks.
              </li>
              <li>
                Send unsolicited commercial email (“spam”) or messages that violate
                the CAN-SPAM Act or other anti-spam laws.
              </li>
              <li>
                Host or distribute child sexual abuse material or other illegal
                content; we report suspected violations to law enforcement as
                required or permitted by law.
              </li>
            </ul>
            <p>
              We may investigate violations and may suspend or terminate services for
              breach of this section or as required by law.
            </p>
          </TermsBlock>

          <TermsBlock title="5. Your content & data">
            <p>
              You retain ownership of content you upload or store on our services
              (“Customer Content”). You grant us a limited license to host, copy,
              transmit, and display Customer Content solely to provide and improve the
              services you requested. You represent that you have all rights necessary
              to provide Customer Content and that it does not violate law or
              third-party rights. Our{" "}
              <a href="/privacy-policy">Privacy Policy</a> explains how we handle
              personal information under U.S. privacy laws.
            </p>
          </TermsBlock>

          <TermsBlock title="6. DMCA & copyright">
            <p>
              We respect intellectual property rights. If you believe material on our
              services infringes your copyright under the U.S. Digital Millennium
              Copyright Act (“DMCA”), you may send a notice that complies with 17
              U.S.C. § 512(c)(3) to the contact email below, including: identification
              of the work, identification of the material and its location, your
              contact information, a good-faith statement, and a statement under
              penalty of perjury that you are authorized to act. We may remove or
              disable access to material in appropriate cases and terminate repeat
              infringers where required by law.
            </p>
          </TermsBlock>

          <TermsBlock title="7. Fees, taxes & billing">
            <p>
              Fees are stated in U.S. dollars unless otherwise specified. You agree
              to pay all fees for the services you order, plus applicable sales, use,
              or similar taxes as required by U.S. state and local law. Late payments
              may incur interest or suspension of services as permitted by law and your
              Service Order. Unless your agreement states otherwise, fees are
              non-refundable except where required by law or expressly stated in
              writing.
            </p>
          </TermsBlock>

          <TermsBlock title="8. Service levels & backups">
            <p>
              Unless a separate Service Level Agreement (“SLA”) applies, services are
              provided on an “as available” basis. You are responsible for maintaining
              your own backups and recovery plans unless your plan expressly includes
              backup services. Uptime targets, if any, are described in your Service
              Order or product page.
            </p>
          </TermsBlock>

          <TermsBlock title="9. Suspension & termination">
            <p>
              We may suspend or terminate access for non-payment, breach of these
              Terms, legal requirement, or risk to security or third parties. You may
              cancel according to your Service Order. Provisions that by their nature
              should survive (including disclaimers, limitations, indemnity, and
              governing law) will survive termination.
            </p>
          </TermsBlock>

          <TermsBlock title="10. Disclaimers">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY U.S. LAW, EXCEPT AS EXPRESSLY SET
              FORTH IN A WRITTEN SLA, OUR SERVICES ARE PROVIDED “AS IS” AND “AS
              AVAILABLE,” WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED,
              OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
              FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. SOME STATES DO
              NOT ALLOW CERTAIN DISCLAIMERS; IN THOSE STATES, OUR LIABILITY IS
              LIMITED TO THE EXTENT PERMITTED BY LAW.
            </p>
          </TermsBlock>

          <TermsBlock title="11. Limitation of liability">
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE U.S. LAW, NEITHER{" "}
              {brand.name} NOR ITS SUPPLIERS WILL BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOSS OF
              PROFITS, DATA, GOODWILL, OR BUSINESS OPPORTUNITY. OUR AGGREGATE
              LIABILITY FOR ANY CLAIM ARISING OUT OF THESE TERMS OR THE SERVICES WILL
              NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID US FOR THE SERVICES
              GIVING RISE TO THE CLAIM DURING THE TWELVE (12) MONTHS BEFORE THE CLAIM,
              OR (B) ONE HUNDRED U.S. DOLLARS (US $100), EXCEPT WHERE PROHIBITED BY
              LAW. THESE LIMITATIONS APPLY WHETHER THE CLAIM IS BASED IN CONTRACT,
              TORT, STATUTE, OR ANY OTHER THEORY.
            </p>
          </TermsBlock>

          <TermsBlock title="12. Indemnity">
            <p>
              You will defend, indemnify, and hold harmless {brand.name} and its
              affiliates, officers, and employees from any third-party claims, damages,
              or costs (including reasonable attorneys’ fees) arising from Customer
              Content, your use of the services in violation of these Terms or law, or
              your violation of third-party rights.
            </p>
          </TermsBlock>

          <TermsBlock title="13. Governing law & venue">
            <p>
              These Terms are governed by the laws of the United States and the State
              of Delaware, without regard to conflict-of-law rules. You and we agree
              that the state and federal courts located in Delaware will have
              exclusive jurisdiction for disputes arising from these Terms or the
              services, subject to mandatory consumer protections in your state of
              residence where applicable.
            </p>
          </TermsBlock>

          <TermsBlock title="14. Electronic communications">
            <p>
              You consent to receive communications from us electronically (including
              email and notices on our website). You agree that such communications
              satisfy any legal writing requirements, including under the U.S.
              Electronic Signatures in Global and National Commerce Act (E-SIGN) and
              similar state laws, where applicable.
            </p>
          </TermsBlock>

          <TermsBlock title="15. Changes">
            <p>
              We may update these Terms by posting a revised version and updating the
              “last updated” date. Material changes may require additional notice
              where required by law. Continued use after the effective date constitutes
              acceptance of the updated Terms.
            </p>
          </TermsBlock>

          <TermsBlock title="16. Contact">
            <ul className="terms-contact">
              <li>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${brand.email}`}>{brand.email}</a>
              </li>
              <li>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${brand.phonecall}`}>{brand.phone}</a>
              </li>
              <li>
                <strong>Mailing address:</strong> {brand.address}
              </li>
            </ul>
            <p className="terms-footnote">
              If you are a California resident, see our Privacy Policy for
              information about rights under the California Consumer Privacy Act
              (CCPA), as amended by the CPRA.
            </p>
          </TermsBlock>
        </div>
      </div>
    </section>
  );
};

const TermsBlock = ({ title, children }) => (
  <section className="terms-card">
    <h2>{title}</h2>
    {children}
  </section>
);

export default Terms;
